import { Link } from "react-router-dom";
import { formatBoon, formatRelative, formatUsdc, shortAddr } from "../../lib/api";
import { reviewStableKey, type X402PublicReview, type X402ReviewerReviewItem } from "../../lib/x402Discovery";
import { preferredReviewerAccount } from "./X402ReviewerIdentities";

export function X402ReviewPanel({
  review,
  embedded = false,
  otherReviews = [],
}: {
  review: X402PublicReview;
  embedded?: boolean;
  otherReviews?: X402ReviewerReviewItem[];
}) {
  const accounts = review.reviewer.accounts;
  const reviewerHref = "/x402/reviewers/" + review.reviewer.wallet;
  const primaryAccount = preferredReviewerAccount(accounts);
  const profileHref = primaryAccount ? "/p/" + encodeURIComponent(primaryAccount.handle) : reviewerHref;
  const boonBacked = review.evidenceKind === "boon_backed";
  const receiptVerified = review.evidenceKind === "receipt_verified";
  return (
    <article className={embedded ? "" : "border border-faint bg-paper rounded-md p-5 md:p-6"}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="btn-mono text-[10px] uppercase tracking-[0.14em] text-olive-deep">
            {boonBacked ? "Boon-backed review" : receiptVerified ? "Receipt-verified usage review" : "Self-reported review"}
          </p>
          <Link
            className="group mt-3 flex max-w-xl items-center gap-3 rounded-lg border border-faint bg-paper-deep/45 p-3 transition-colors hover:border-olive/45 hover:bg-olive-soft/35"
            to={profileHref}
            aria-label={`Reviewer profile, wallet ${review.reviewer.wallet}`}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-olive-soft font-display text-lg text-olive-deep" aria-hidden="true">
              {(primaryAccount?.handle ?? shortAddr(review.reviewer.wallet)).replace(/^[^:]+:/, "").slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0">
              <span className="block text-xs text-muted">Reviewer profile</span>
              <span className="mt-0.5 block truncate text-sm font-semibold text-ink group-hover:text-olive-deep">
                {accounts.length > 0 ? accounts.map((account) => account.handle).join(", ") : shortAddr(review.reviewer.wallet)}
              </span>
              <span className="mt-1 block truncate btn-mono text-[10px] text-muted">{shortAddr(review.reviewer.wallet)} · View profile →</span>
            </span>
          </Link>
        </div>
        <span className="btn-mono text-[10px] text-muted">
          {boonBacked ? "ranked conviction" : "visible volume · unranked"}
        </span>
      </div>
      <blockquote className="mt-5 border-l-4 border-olive pl-5 text-2xl md:text-3xl font-display leading-snug text-ink">
        “{review.reviewText}”
      </blockquote>
      {boonBacked ? (
        <>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4 border-y border-faint py-4">
            <ReviewFact label="USDC gratitude" value={formatUsdc(review.recognition.totalRecognitionUsdc)} />
            <ReviewFact label="$BOON burned" value={formatBoon(review.recognition.boonBurned)} />
            <ReviewFact label="Published" value={formatRelative(String(review.createdAt))} />
          </div>
          <details className="mt-2 text-xs text-muted">
            <summary className="cursor-pointer inline-flex min-h-9 items-center btn-mono text-[10px] text-muted hover:text-olive-deep">Verification details</summary>
            <a
              className="ml-3 inline-flex min-h-9 items-center btn-mono text-[10px] text-olive-deep hover:underline"
              href={"https://basescan.org/tx/" + review.recognition.transactionHash}
              target="_blank"
              rel="noopener noreferrer"
            >
              Verify on BaseScan ↗
            </a>
          </details>
        </>
      ) : receiptVerified ? (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 border-y border-faint py-4">
          <ReviewFact label="Evidence" value="x402 payer receipt" />
          <ReviewFact label="Cited offer amount" value={review.receipt.amount + " base units"} />
          <ReviewFact label="Published" value={formatRelative(String(review.createdAt))} />
          {review.receipt.transaction ? (
            <a
              className="min-h-11 inline-flex flex-col justify-center btn-mono text-xs text-olive-deep hover:underline"
              href={"https://basescan.org/tx/" + review.receipt.transaction}
              target="_blank"
              rel="noopener noreferrer"
            >
              View payment ↗
            </a>
          ) : <ReviewFact label="Ranking" value="excluded" />}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 border-y border-faint py-4">
          <ReviewFact label="Evidence" value="wallet signature only" />
          <ReviewFact label="Publication" value="$0.05 anti-spam" />
          <ReviewFact label="Published" value={formatRelative(String(review.createdAt))} />
          <ReviewFact label="Ranking" value="excluded" />
        </div>
      )}
      {otherReviews.length > 0 && (
        <div className="mt-5">
          <p className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">Other reviews from this wallet</p>
          <div className="mt-3 grid gap-2">
            {otherReviews.slice(0, 3).map((item) => (
              <Link
                key={item.routeId + ":" + reviewStableKey(item.review)}
                className="group grid gap-1 rounded-md bg-paper-deep/55 p-3 hover:bg-olive-soft transition-colors"
                to={"/x402/routes/" + item.routeId}
              >
                <span className="text-sm font-semibold group-hover:text-olive-deep">
                  {item.description?.summary ?? "Route " + item.routeId.slice(0, 10) + "…"}
                </span>
                <span className="text-sm text-ink-soft">“{item.review.reviewText}”</span>
              </Link>
            ))}
          </div>
        </div>
      )}
      <Link className="mt-4 inline-flex min-h-11 items-center btn-mono text-xs text-olive-deep hover:underline" to={reviewerHref}>
        View all reviews by this wallet →
      </Link>
    </article>
  );
}

function ReviewFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">{label}</p>
      <p className="mt-1 text-base text-ink">{value}</p>
    </div>
  );
}
